import { Capacitor } from '@capacitor/core';
import { registerPlugin } from '@capacitor/core';

/**
 * 推送权限管理插件
 * 用于检查和请求Android推送相关权限
 */
const PushPermission = registerPlugin('PushPermission');

/**
 * 检查所有推送权限状态
 * @returns {Promise<{notificationPermission: boolean, batteryOptimization: boolean, allGranted: boolean}>}
 */
export async function checkPushPermissions() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    console.log('[PushPermission] Not Android native platform, skip permission check');
    return {
      notificationPermission: true,
      batteryOptimization: true,
      allGranted: true
    };
  }

  try {
    const result = await PushPermission.checkPermissions();
    console.log('[PushPermission] Permission status:', result);
    return result;
  } catch (error) {
    console.error('[PushPermission] Error checking permissions:', error);
    return {
      notificationPermission: false,
      batteryOptimization: false,
      allGranted: false
    };
  }
}

/**
 * 请求通知权限（Android 13+）
 * @returns {Promise<{granted: boolean}>}
 */
export async function requestNotificationPermission() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    console.log('[PushPermission] Not Android native platform, skip notification permission request');
    return { granted: true };
  }

  try {
    const result = await PushPermission.requestNotificationPermission();
    console.log('[PushPermission] Notification permission result:', result);
    return result;
  } catch (error) {
    console.error('[PushPermission] Error requesting notification permission:', error);
    return { granted: false };
  }
}

/**
 * 请求电池优化豁免
 * 注意：这会打开系统设置页面，需要用户手动操作
 * @returns {Promise<{success: boolean}>}
 */
export async function requestBatteryOptimization() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    console.log('[PushPermission] Not Android native platform, skip battery optimization request');
    return { success: true };
  }

  try {
    const result = await PushPermission.requestBatteryOptimization();
    console.log('[PushPermission] Battery optimization request result:', result);
    return result;
  } catch (error) {
    console.error('[PushPermission] Error requesting battery optimization:', error);
    return { success: false };
  }
}

/**
 * 打开通知设置页面
 * @returns {Promise<{success: boolean}>}
 */
export async function openNotificationSettings() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    console.log('[PushPermission] Not Android native platform, skip opening notification settings');
    return { success: false };
  }

  try {
    const result = await PushPermission.openNotificationSettings();
    console.log('[PushPermission] Open notification settings result:', result);
    return result;
  } catch (error) {
    console.error('[PushPermission] Error opening notification settings:', error);
    return { success: false };
  }
}

/**
 * 打开应用设置页面
 * @returns {Promise<{success: boolean}>}
 */
export async function openAppSettings() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    console.log('[PushPermission] Not Android native platform, skip opening app settings');
    return { success: false };
  }

  try {
    const result = await PushPermission.openAppSettings();
    console.log('[PushPermission] Open app settings result:', result);
    return result;
  } catch (error) {
    console.error('[PushPermission] Error opening app settings:', error);
    return { success: false };
  }
}

/**
 * 完整的推送权限请求流程
 * 1. 先请求通知权限
 * 2. 再提示用户设置电池优化豁免
 * @param {Object} options - 配置选项
 * @param {boolean} options.showBatteryOptimization - 是否显示电池优化请求（默认true）
 * @returns {Promise<boolean>} - 是否所有权限都已授予
 */
export async function requestAllPushPermissions(options = {}) {
  const { showBatteryOptimization = true } = options;

  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    console.log('[PushPermission] Not Android native platform, skip all permission requests');
    return true;
  }

  try {
    // 1. 先检查当前状态
    const currentStatus = await checkPushPermissions();
    console.log('[PushPermission] Current status before request:', currentStatus);

    // 2. 请求通知权限
    if (!currentStatus.notificationPermission) {
      console.log('[PushPermission] Requesting notification permission...');
      await requestNotificationPermission();
      
      // 等待一下，让权限状态更新
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    // 3. 请求电池优化豁免（可选）
    if (showBatteryOptimization && !currentStatus.batteryOptimization) {
      console.log('[PushPermission] Requesting battery optimization exemption...');
      await requestBatteryOptimization();
    }

    // 4. 再次检查状态
    const finalStatus = await checkPushPermissions();
    console.log('[PushPermission] Final status after request:', finalStatus);

    return finalStatus.allGranted;
  } catch (error) {
    console.error('[PushPermission] Error in requestAllPushPermissions:', error);
    return false;
  }
}

export default {
  checkPushPermissions,
  requestNotificationPermission,
  requestBatteryOptimization,
  openNotificationSettings,
  openAppSettings,
  requestAllPushPermissions
};
